import PermissionButton from '../../../permissions/PermissionButton';
import React, { useState, useMemo, useEffect } from 'react';
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Tabs,
  message,
  Drawer,
  DatePicker,
  Form,
  Tooltip,
  Popconfirm,
} from 'antd';
import {
  SearchOutlined,
  LoadingOutlined,
  PlusOutlined,
  SwapOutlined,
  FilterOutlined,
  ReloadOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useFetchAllCommandesAchat, useCreateCommandeAchat, useValidateCommandeAchat, useTransferCommandeToReception } from '../../../hooks/achatCommandeHooks';
import { useFetchAllFours } from '../../../hooks/fourHooks';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

// Constants for localStorage
const ACTIVE_TAB_KEY = 'commande_achat_active_tab';

const CommandeList = ({ onSelect, userPermissions = { isManager: false, isDevisManager: false } }) => {
  const [fournisseurSearch, setFournisseurSearch] = useState('');
  const [refSearch, setRefSearch] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

  // Initialize activeTab from localStorage or default to 'notValidated'
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem(ACTIVE_TAB_KEY);
    return savedTab || 'notValidated';
  });

  // Initialize sorter based on activeTab
  const [sorter, setSorter] = useState(() => {
    const savedTab = localStorage.getItem(ACTIVE_TAB_KEY) || 'notValidated';
    return {
      field: 'REF_CA',
      order: savedTab === 'transferred' ? 'descend' : 'ascend'
    };
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isTransferring, setIsTransferring] = useState(false);
  const [form] = Form.useForm();
  const [hoveredRow, setHoveredRow] = useState(null);

  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
  }, [activeTab]);

  const params = useMemo(() => ({
    page: pagination.current,
    pageSize: pagination.pageSize,
    frsSearch: fournisseurSearch,
    refSearch,
    dateFrom: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
    dateTo: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
    sortField: sorter.field,
    sortOrder: sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined,
    status: activeTab,
  }), [pagination.current, pagination.pageSize, fournisseurSearch, refSearch, dateRange, sorter.field, sorter.order, activeTab]);

  const { items, total, loading, error, refetch } = useFetchAllCommandesAchat(params);
  const { validate } = useValidateCommandeAchat();
  const { transfer } = useTransferCommandeToReception();

  const rowSelection = activeTab === 'validated' ? {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.TRANSFERER,
    }),
    onCell: () => ({
      onClick: e => {
        e.stopPropagation();
      }
    }),
  } : undefined;

  // Handle row click
  const handleRowClick = (record) => {
    onSelect(record.id);
  };

  const referenceColumn = {
    title: 'Ref Cmd.',
    dataIndex: 'REF_CA',
    key: 'REF_CA',
    sorter: true,
    defaultSortOrder: activeTab === 'transferred' ? 'descend' : 'ascend',
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder="Commence par"
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            setRefSearch(selectedKeys[0]);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          style={{ marginBottom: 8, display: 'block' }}
          ref={node => node && setTimeout(() => node.focus(), 0)}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              setRefSearch(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Rechercher
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              setRefSearch('');
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            size="small"
            style={{ width: 90 }}
          >
            Réinitialiser
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    render: (text, record) => (
      <span
        style={{
          position: 'relative',
          fontWeight: hoveredRow === record.id ? 'bold' : 'normal',
          transition: 'font-weight 0.3s ease'
        }}
      >
        {text}
        <span
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: hoveredRow === record.id ? '100%' : '0%',
            height: '1px',
            backgroundColor: '#000',
            transition: 'width 0.3s ease'
          }}
        />
      </span>
    )
  };

  const dateColumn = {
    title: activeTab === 'notValidated' ? 'Date Cmd.' : 'Date Valid.',
    dataIndex: 'DATE_CMD',
    key: 'date',
    render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '',
    sorter: true,
    onCell: () => ({
      onClick: e => {
        e.stopPropagation();
        return false;
      }
    })
  };

  const fournisseurColumn = {
    title: 'Fournisseur',
    dataIndex: 'FOURNISSEUR',
    key: 'FOURNISSEUR',
    width: 400,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder="Rechercher Fournisseur"
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            setFournisseurSearch(selectedKeys[0]);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          style={{ marginBottom: 8, display: 'block' }}
          ref={node => node && setTimeout(() => node.focus(), 0)}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              setFournisseurSearch(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Rechercher
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              setFournisseurSearch('');
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            size="small"
            style={{ width: 90 }}
          >
            Réinitialiser
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
  };

  const priceColumnHT = {
    title: 'Total HT',
    dataIndex: 'MNT_HT',
    key: 'MNT_HT',
    render: value => value?.toFixed(2) || '0.00',
    sorter: true,
  };

  const priceColumnTTC = {
    title: 'Total TTC',
    dataIndex: 'MNT_TTC',
    key: 'MNT_TTC',
    render: value => value?.toFixed(2) || '0.00',
    sorter: true,
  };

  const handleValidate = async (record) => {
    try {
      await validate(record.id);
      message.success('Commande validée avec succès');
      refetch();
    } catch (e) {
      message.error(`Échec de la validation: ${e.message || e}`);
    }
  };

  const columns = [
    dateColumn,
    referenceColumn,
    fournisseurColumn,
    priceColumnHT,
    priceColumnTTC,
    {
      title: 'Remarque',
      dataIndex: 'REMARQUE',
      key: 'REMARQUE',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => {
        const canValidate = activeTab === 'notValidated';
        return (
          <Space>
            {canValidate && (
              <PermissionButton permissionModule="purchaseOrders" permissionAction="validate"
                type="primary"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleValidate(record);
                }}
              >
                Valider
              </PermissionButton>
            )}
          </Space>
        );
      },
      onCell: () => ({
        onClick: e => e.stopPropagation(),
      }),
    },
  ];

  const handleBulkTransfer = async () => {
    if (!selectedRowKeys.length) {
      message.warning('Veuillez sélectionner des commandes à transférer');
      return;
    }

    setIsTransferring(true);
    try {
      for (const id of selectedRowKeys) {
        const record = items.find(item => item.id === id);
        if (record?.REF_CA) {
          await transfer(record.REF_CA);
        }
      }
      message.success(`${selectedRowKeys.length} commande(s) transférée(s) avec succès`);
      setSelectedRowKeys([]);
      refetch();
    } catch (error) {
      message.error('Échec du transfert de certaines commandes: ' + error.message);
    } finally {
      setIsTransferring(false);
    }
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSelectedRowKeys([]);

    // Update the sorter based on the active tab
    setSorter({
      field: 'REF_CA',
      order: newTab === 'transferred' ? 'descend' : 'ascend'
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(pagination);
    setSorter(sorter);
  };

  const getRowStyle = (record) => {
    if (record.TRANSFERER) {
      return { backgroundColor: '#e6f7ff' };
    }
    if (activeTab === 'validated') {
      return { backgroundColor: '#f6ffed' };
    }
    return {};
  };

  const handleDateChange = (dates) => {
    setDateRange(dates || [null, null]);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const clearAllFilters = () => {
    setFournisseurSearch('');
    setRefSearch('');
    setDateRange([null, null]);
    form.resetFields();
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleRefetch = async () => {
    try {
      await refetch();
      message.success('Liste des commandes actualisée');
    } catch (error) {
      message.error(`Échec de l'actualisation: ${error?.message || 'Erreur inconnue'}`);
    }
  };

  return (
    <div className="commande-list-container">
      <Card
        style={{
          marginTop: 20,
          backgroundColor: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
        className="commande-card"
      >
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', margin: 0 }}>Liste Des Commandes d'Achat</h2>
            <Tooltip title="Actualiser la liste">
              <Button
                onClick={handleRefetch}
                icon={<ReloadOutlined />}
                type="text"
              />
            </Tooltip>
          </div>
          <Space>
            {activeTab === 'validated' && selectedRowKeys.length > 0 && (
              <PermissionButton permissionModule="purchaseOrders" permissionAction="transfer"
                type="primary"
                icon={<SwapOutlined />}
                onClick={handleBulkTransfer}
                loading={isTransferring}
              >
                Transférer Sélectionnés ({selectedRowKeys.length})
              </PermissionButton>
            )}
            <PermissionButton permissionModule="purchaseOrders" permissionAction="create"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsDrawerOpen(true)}
            >
              Créer Commande
            </PermissionButton>
          </Space>
        </Space>

        <Form
          form={form}
          layout="inline"
          style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}
        >
          <Form.Item
            name="dateRange"
            label="Période"
            style={{ display: 'inline-flex', alignItems: 'center', marginRight: 6, marginBottom: 4 }}
            labelCol={{ style: { display: 'flex', alignItems: 'center', paddingBottom: 0 } }}
          >
            <RangePicker
              onChange={handleDateChange}
              value={dateRange}
              format="DD/MM/YYYY"
              placeholder={['Date début', 'Date fin']}
              allowClear
              size="small"
              style={{ width: 260 }}
            />
          </Form.Item>
          <Form.Item style={{ marginRight: 6, marginBottom: 4 }}>
            <Tooltip title="Réinitialiser période">
              <Button
                icon={<FilterOutlined />}
                onClick={() => {
                  setDateRange([null, null]);
                  form.resetFields(['dateRange']);
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
                size="small"
              />
            </Tooltip>
          </Form.Item>
        </Form>

        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="Enregistrés" key="notValidated" />
          <TabPane tab="Validés" key="validated" />
          <TabPane tab="Transférés" key="transferred" />
        </Tabs>

        <Table
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            onMouseEnter: () => setHoveredRow(record.id),
            onMouseLeave: () => setHoveredRow(null),
            style: { ...getRowStyle(record), cursor: 'pointer' },
            className: 'commande-table-row',
          })}
          rowSelection={rowSelection}
          className="commande-table"
          size="small"
        />

        <Drawer
          title="Créer Nouvelle Commande"
          width={920}
          onClose={() => setIsDrawerOpen(false)}
          open={isDrawerOpen}
          bodyStyle={{ paddingBottom: 80 }}
        >
          <CommandeForm onCommandeCreated={(id) => { setIsDrawerOpen(false); onSelect(id); }} />
        </Drawer>
      </Card>
    </div>
  );
};

export default CommandeList;

// Inline Commande creation form
const CommandeForm = ({ onCommandeCreated }) => {
  const { Fours: fours, loading: ldg } = useFetchAllFours();
  const { create } = useCreateCommandeAchat();
  const [selectedFour, setSelectedFour] = useState(null);
  const [search, setSearch] = useState('');

  const data = (fours || []).filter(f => {
    const q = (search || '').toLowerCase();
    return !q || f.sociale?.toLowerCase().includes(q) || f.compte?.toLowerCase().includes(q);
  });

  const columns = [
    { title: 'Compte', dataIndex: 'compte', key: 'compte' },
    { title: 'Fournisseur', dataIndex: 'sociale', key: 'sociale' },
    { title: 'Pays', dataIndex: 'pays', key: 'pays' },
  ];

  const handleCreate = async () => {
    try {
      if (!selectedFour) {
        message.error('Veuillez sélectionner un fournisseur');
        return;
      }
      const res = await create({
        CODE_FRS: selectedFour.code_frs,
        FOURNISSEUR: selectedFour.sociale,
        DATE_CMD: new Date(),
      });
      message.success(`Commande créée: ${res.REF_CA || res.id}`);
      if (onCommandeCreated) {
        onCommandeCreated(res.id);
      }
    } catch (e) {
      message.error(e.message || 'Erreur création');
    }
  };

  return (
    <div style={{ display:'grid', gap:12 }}>
      <div style={{ padding: '12px', background:'#f0f2f5', borderRadius: 6 }}>
        <div style={{ color: selectedFour ? '#000' : '#999', marginBottom: 6 }}>
          {selectedFour ? 'Fournisseur sélectionné' : 'Veuillez sélectionner un fournisseur'}
        </div>
        {selectedFour && (
          <div>
            <b>{selectedFour.sociale}</b> — Compte: {selectedFour.compte || 'N/A'}
          </div>
        )}
      </div>
      <Input
        placeholder="Rechercher fournisseur (compte, nom)"
        value={search}
        onChange={e=>setSearch(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      <Table
        size="small"
        rowKey="code_frs"
        loading={ldg}
        dataSource={data}
        columns={columns}
        pagination={{ pageSize: 8 }}
        onRow={(record)=> ({
          onClick: ()=> setSelectedFour(record),
          style: {
            cursor:'pointer',
            backgroundColor: selectedFour?.code_frs === record.code_frs ? '#e6f7ff' : undefined
          }
        })}
      />
      <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
        <PermissionButton permissionModule="purchaseOrders" permissionAction="create" type="primary" onClick={handleCreate} disabled={!selectedFour}>
          Créer Commande
        </PermissionButton>
      </Space>
    </div>
  );
};
